/* eslint-disable react/prop-types */
/**
 * /projects/workbench — Iter 236.
 *
 * Top-level index of all "future projects" being brainstormed, planned
 * or estimated. Lets reps return to any draft, see at-a-glance which
 * stage each is in, and start a brand-new one in two clicks.
 *
 * Trust Contract: filters live as inline pills; create-new uses an
 * inline composer panel (no modal). Each row links straight to the
 * draft's detail page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Sparkles, ClipboardList, Calculator,
  ArrowRight, Search, Loader2, Archive, RotateCcw, BookOpen,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import {
  WORKBENCH_KINDS, labelForKind, normaliseCustomKind,
} from "../lib/workbenchKinds";
import { PresetLibraryDialog, PresetPicker, usePresets } from "../components/workbench/PromptPresets";

const STAGE_META = {
  brainstorm: {
    label: "Brainstorm",
    color: "#A78BFA",
    icon: Sparkles,
    blurb: "Chatting through the system requirements.",
  },
  plan: {
    label: "Planning",
    color: "#1FB6C1",
    icon: ClipboardList,
    blurb: "Components mapped to inventory.",
  },
  estimate: {
    label: "Estimating",
    color: "#F59E0B",
    icon: Calculator,
    blurb: "Building the priced quote draft.",
  },
};

const FILTERS = [
  { key: "all", label: "All active" },
  { key: "brainstorm", label: "Brainstorming" },
  { key: "plan", label: "Planning" },
  { key: "estimate", label: "Estimating" },
  { key: "archived", label: "Archived" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function ProjectWorkbench() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [composerName, setComposerName] = useState("");
  const [composerBusy, setComposerBusy] = useState(false);
  // Iter 428.p7 — kind selector on the standalone workbench index so
  // reps can start a report / financial breakdown / whatever WITHOUT
  // first attaching a project (Dirk's ask: same purpose flag works
  // whether the session is project-linked or free-standing).
  const [composerKind, setComposerKind] = useState("proposal");
  const [composerCustom, setComposerCustom] = useState("");
  const presets = usePresets();
  const [presetId, setPresetId] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") {
        if (filter === "archived") params.status = "archived";
        else params.stage = filter;
      }
      const { data } = await api.get("/workbench/drafts", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (r.name || "").toLowerCase().includes(s));
  }, [rows, q]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    let kindOnWire = composerKind;
    if (composerKind === "__custom__") {
      const norm = normaliseCustomKind(composerCustom);
      if (!norm) {
        toast.error("Enter a custom kind (letters + underscores).");
        return;
      }
      kindOnWire = norm;
    }
    setComposerBusy(true);
    try {
      const { data } = await api.post("/workbench/drafts", {
        name: composerName.trim() || undefined,
        kind: kindOnWire,
      });
      toast.success(`New ${labelForKind(kindOnWire)} started.`);
      const preset = presets.items.find((p) => p.id === presetId);
      if (preset) await api.post(`/workbench/presets/${preset.id}/use`, { draft_id: data.id }).catch(() => {});
      navigate(`/projects/workbench/${data.id}`, { state: preset ? { presetPrompt: preset.prompt } : undefined });
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not start a workbench.");
    } finally {
      setComposerBusy(false);
    }
  };

  const handleArchive = async (did) => {
    try {
      await api.patch(`/workbench/drafts/${did}`, { status: "archived" });
      toast.success("Archived.");
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Archive failed.");
    }
  };

  const handleRestore = async (did) => {
    try {
      await api.patch(`/workbench/drafts/${did}`, { status: "active" });
      toast.success("Restored.");
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Restore failed.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="workbench-index">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-cyan-300" />
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
            Project Workbench
          </h1>
        </div>
        <p className="text-sm text-[#a0aabf] max-w-2xl">
          One place to brainstorm, plan and estimate <span className="italic">future</span>{" "}
          projects. A draft can live customer-less while you think it through — promote it to a
          real project the moment you're ready.
        </p>
      </header>

      {/* New-draft composer (Trust Contract: inline, no modal) */}
      {creating ? (
        <form
          onSubmit={handleCreate}
          className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 p-3 space-y-2"
          data-testid="workbench-composer"
        >
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf]">
              Working title (optional — you can rename anytime)
            </span>
            <input
              autoFocus
              value={composerName}
              onChange={(e) => setComposerName(e.target.value)}
              placeholder="e.g. Lodge pump-house upgrade"
              className="hv-input mt-1 text-sm"
              data-testid="workbench-composer-name"
            />
          </label>
          {/* Iter 428.p7 — Kind picker. Works without a project link;
              the same purpose flag carries over when the session is
              later attached to a project. */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#a0aabf] mb-1">
              What is this session?
            </div>
            <div className="flex gap-1 flex-wrap">
              {WORKBENCH_KINDS.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => setComposerKind(k.key)}
                  className={`text-[10px] px-2 py-0.5 rounded border ${
                    composerKind === k.key
                      ? "border-cyan-400 bg-cyan-500/10 text-cyan-200"
                      : "border-white/10 text-[#cbd2df] hover:border-cyan-400/40"
                  }`}
                  data-testid={`workbench-composer-kind-${k.key}`}
                >
                  {k.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setComposerKind("__custom__")}
                className={`text-[10px] px-2 py-0.5 rounded border ${
                  composerKind === "__custom__"
                    ? "border-cyan-400 bg-cyan-500/10 text-cyan-200"
                    : "border-white/10 text-[#cbd2df] hover:border-cyan-400/40"
                }`}
                data-testid="workbench-composer-kind-custom"
              >
                + Add other
              </button>
            </div>
            {composerKind === "__custom__" && (
              <input
                value={composerCustom}
                onChange={(e) => setComposerCustom(e.target.value)}
                placeholder="Custom kind name (e.g. site_survey)"
                className="hv-input mt-2 text-sm"
                data-testid="workbench-composer-kind-custom-input"
              />
            )}
          </div>
          <PresetPicker items={presets.items} kind={composerKind} value={presetId} onChange={setPresetId} onManage={() => setLibraryOpen(true)} />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setCreating(false); setComposerName(""); setComposerCustom(""); }}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="workbench-composer-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={composerBusy}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="workbench-composer-submit"
            >
              {composerBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Start session
            </button>
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCreating(true)}
            className="hv-btn hv-btn-primary text-sm"
            data-testid="workbench-new-btn"
          >
            <Plus className="w-4 h-4" /> Start a new brainstorm
          </button>
          <button onClick={() => setLibraryOpen(true)} className="hv-btn hv-btn-ghost text-sm" data-testid="workbench-prompt-library-btn">
            <BookOpen className="w-4 h-4" /> Prompt library
          </button>
        </div>
      )}
      <PresetLibraryDialog open={libraryOpen} onOpenChange={setLibraryOpen} onChanged={presets.reload} />

      {/* Filters + search */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1" data-testid="workbench-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`workbench-filter-${f.key}`}
              className={`px-3 py-1 text-xs rounded-full border transition ${
                filter === f.key
                  ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-100"
                  : "border-white/10 bg-transparent text-[#a0aabf] hover:border-white/25"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search drafts…"
            className="hv-input text-xs pl-7 py-1.5 w-44 sm:w-56"
            data-testid="workbench-search"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-[#647087]" data-testid="workbench-loading">Loading drafts…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-[#647087]" data-testid="workbench-empty">
          {q
            ? "No drafts match your search."
            : filter === "archived"
            ? "No archived drafts yet."
            : "Nothing in flight here yet. Start a brainstorm above and we'll keep the trail."}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="workbench-list">
          {filtered.map((r) => {
            const meta = STAGE_META[r.current_stage] || STAGE_META.brainstorm;
            const Icon = meta.icon;
            const isArchived = r.status === "archived";
            const isPromoted = r.status === "promoted";
            return (
              <li
                key={r.id}
                className="rounded-lg border border-white/10 bg-white/5 hover:border-cyan-400/40 transition group"
                data-testid={`workbench-row-${r.id}`}
              >
                <div className="flex items-center gap-3 p-3">
                  <div
                    className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: `${meta.color}22`, color: meta.color }}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <Link to={`/projects/workbench/${r.id}`} className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      <span className="truncate">{r.name}</span>
                      {/* Iter 428.p7 — Purpose chip so free-standing
                          workbenches surface their kind at a glance. */}
                      <span
                        className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border shrink-0 border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-200"
                        data-testid={`workbench-row-kind-${r.id}`}
                      >
                        {labelForKind(r.kind || "proposal")}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#647087] truncate">
                      <span style={{ color: meta.color }}>{meta.label}</span>
                      {" · "}{meta.blurb}
                      {r.customer_id && <> · <span className="text-[#a0aabf]">customer linked</span></>}
                      {isPromoted && <> · <span className="text-emerald-300">promoted to project</span></>}
                    </div>
                  </Link>
                  <div className="text-[10px] text-[#647087] hidden sm:block tabular-nums">
                    {timeAgo(r.updated_at)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isPromoted && r.project_id && (
                      <Link
                        to={`/projects/${r.project_id}`}
                        className="hv-btn hv-btn-ghost text-[10px] px-2 py-1"
                        data-testid={`workbench-open-project-${r.id}`}
                      >
                        Open project <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                    {!isArchived && !isPromoted && (
                      <button
                        type="button"
                        onClick={() => handleArchive(r.id)}
                        title="Archive"
                        className="text-[#647087] hover:text-rose-300 p-1"
                        data-testid={`workbench-archive-${r.id}`}
                      >
                        <Archive className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {isArchived && (
                      <button
                        type="button"
                        onClick={() => handleRestore(r.id)}
                        title="Restore"
                        className="text-[#647087] hover:text-cyan-300 p-1"
                        data-testid={`workbench-restore-${r.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
